/**
 * @file Renderização da página 404 compartilhada do EP.
 * @module ep/tools/notFound
 *
 * A página 404 reutiliza os tokens visuais do tema ativo. O renderer altera
 * apenas classes de estado/layout necessárias para assumir a tela inteira e
 * preserva `light`, `dark` e `system` no `<body>`.
 */

import create from '@ep/components/ep';
import notFoundIllustration from '@ep/images/404.webp';

/**
 * Resolve o caminho inicial respeitando o idioma ativo da aplicação.
 *
 * @param {string} [fallbackPath='/'] Caminho padrão quando o idioma ativo é o padrão.
 * @returns {string} Caminho inicial localizado.
 * @private
 */
const resolveLocalizedHomePath = (fallbackPath = '/') => {
  const languageConfig = window?.language && typeof window.language === 'object' ? window.language : {};
  const defaultLang = typeof languageConfig.default === 'string' && languageConfig.default.trim()
    ? languageConfig.default.trim()
    : 'pt-BR';
  const supportedLangs = Array.isArray(languageConfig.supported) && languageConfig.supported.length > 0
    ? languageConfig.supported
    : [defaultLang];
  const currentLang = String(document.documentElement?.lang || defaultLang).trim();

  if (!supportedLangs.includes(currentLang) || currentLang === defaultLang) {
    return fallbackPath;
  }

  return `/${currentLang}`;
};

/**
 * Renderiza a página 404 da Platform preservando o tema visual ativo.
 *
 * O renderer limpa o conteúdo corrente, remove somente classes transitórias de
 * layout/roteamento e adiciona `notFound`. As classes `light`, `dark` e `system`
 * são deliberadamente preservadas para que `$page-background`,
 * `$widget-content-background`, cores de texto e botões continuem usando os
 * tokens do tema selecionado.
 *
 * @param {Object} [dic={}] Dicionário ativo da aplicação.
 * @param {Object} [dic.labels] Labels localizados usados pela página.
 * @param {string} [dic.labels.notFoundPage] Título localizado do erro.
 * @param {string} [dic.labels.notFoundPage2] Texto explicativo localizado.
 * @param {string} [dic.labels.notFoundPage3] Rótulo da ação de retorno.
 * @param {string} [navigateTo='/'] Caminho base da ação de retorno.
 * @returns {Promise<void>}
 */
export default async function renderPlatformNotFound(dic = {}, navigateTo = '/') {
  const labels = dic?.labels || {};
  const title = labels.notFoundPage || 'Página não encontrada';
  const message = labels.notFoundPage2 || '';
  const actionText = labels.notFoundPage3 || 'Voltar';
  const localizedHomePath = resolveLocalizedHomePath(navigateTo);

  document.body.classList.remove('lgd', 'uld', 'route-switching');
  document.body.classList.add('notFound');
  document.body.innerHTML = '';
  await create([
    { element: 'div', ident: 'main', parent: 'body' },
    { element: 'div', ident: 'first', parent: ':main' },
    { element: 'div', ident: 'last', parent: ':main' },
    { element: 'h1', parent: ':first', text: '404' },
    { element: 'h2', parent: ':first', text: title },
    { element: 'p', parent: ':first', html: message },
    {
      element: 'button',
      parent: ':first',
      text: actionText,
      class: 'bt-default',
      click: () => {
        window.location.assign(localizedHomePath);
      },
    },
    { element: 'image', parent: ':last', origin: 'external', src: notFoundIllustration, hasFigure: false },
  ]);
}
